<template>
  <div>
    <div
      v-if="showLoader"
      class="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50"
    >
      <div class="animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-yellow-500"></div>
    </div>

    <div v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-sm mt-5">
        <div class="flex items-center justify-between px-4 py-2 bg-white border-b">
          <h3 class="text-base font-semibold text-gray-700">Prescription Queue</h3>
          <span class="text-xs text-gray-500">{{ prescriptions.length }} visit(s)</span>
        </div>
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th
                v-for="(header, index) in tableHeaders"
                :key="index"
                scope="col"
                class="px-6 py-3"
              >
                <div class="flex items-center">{{ header }}</div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(prescription, index) in paginatedData"
              :key="prescription.VisitId"
              class="border-b hover:bg-gray-50"
            >
              <td class="px-6 py-2">{{ index + 1 }}</td>
              <td class="px-6 py-2">{{ prescription.VisitCode }}</td>
              <td class="px-6 py-2">{{ prescription.patient?.unique_code }}</td>
              <td class="px-6 py-2">{{ prescription.patient?.FullName }}</td>
              <td class="px-6 py-2">
                {{ prescription.prescriptions[0]?.requested_by?.Username }}
              </td>
              <td class="px-6 py-2">
                {{ formatDate(prescription.prescriptions[0]?.created_at) }}
              </td>
              <td class="px-6 py-2">
                <span
                  :class="getStatusStyle(getEffectiveStatus(prescription.prescriptions))"
                  class="px-2 py-1 rounded text-xs font-semibold"
                >
                  {{ getEffectiveStatus(prescription.prescriptions) }}
                </span>
              </td>
              <td class="px-6 py-2">
                <button
                  @click="openModal(prescription.VisitId)"
                  class="bg-custom-yellow text-white px-4 py-1 rounded"
                >
                  {{ getActionButtonText(getEffectiveStatus(prescription.prescriptions)) }}
                </button>
              </td>
            </tr>
            <tr v-if="prescriptions.length === 0">
              <td :colspan="tableHeaders.length" class="px-6 py-8 text-center text-gray-400">
                No pending prescriptions
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <PaginationComponent
        v-model:currentPage="currentPage"
        :totalPages="totalPages"
        @update:currentPage="goToPage"
      />
    </div>

    <PharmacyQueueModal
      :show="showModal"
      :prescriptionId="selectedVisitId"
      @close="closeModal"
      @refetch="getPatientPrescriptions"
      @submit="handleAppointmentSubmit"
    />
  </div>
</template>

<script>
import PharmacyQueueModal from "@/views/modals/PharmacyQueueModal.vue";
import PaginationComponent from "@/components/shared/pagination.vue";
import apiClient from "@/services/api";

export default {
  name: "PharmacyQueueTab",
  components: {
    PharmacyQueueModal,
    PaginationComponent,
  },
  props: {
    searchQuery: { type: String, default: "" },
    searchFilters: { type: Object },
  },
  data() {
    return {
      showModal: false,
      tableHeaders: [
        "#",
        "Visit Code",
        "Patient Unique Code",
        "Patient Name",
        "Prescription By",
        "Date of Prescription",
        "Status",
        "Action",
      ],
      prescriptions: [],
      currentPage: 1,
      itemsPerPage: 10,
      showLoader: false,
      selectedVisitId: null,
    };
  },
  mounted() {
    this.getPatientPrescriptions();
  },
  watch: {
    searchFilters: {
      handler(newFilters) {
        if (Object.keys(newFilters).length > 0) {
          this.triggerSearch();
        }
      },
      deep: true,
    },
  },
  computed: {
    totalPages() {
      if (!this.prescriptions.length || !this.itemsPerPage) return 0;
      return Math.ceil(this.prescriptions.length / this.itemsPerPage);
    },
    paginatedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.prescriptions.slice(start, start + this.itemsPerPage);
    },
  },
  methods: {
    goToPage(pageNumber) { this.currentPage = pageNumber; },

    getEffectiveStatus(prescriptions) {
      const hasPending = prescriptions.some(
        p => p.status?.name?.toLowerCase() === "pending"
      );
      if (hasPending) return "Pending";
      return prescriptions[0]?.status?.name || "Unknown";
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      return new Date(dateString).toLocaleString("en-US", {
        year: "numeric", month: "long", day: "numeric",
        hour: "numeric", minute: "numeric", hour12: true,
      });
    },

    getStatusStyle(status) {
      return status === "Pending" ? "bg-red-100 text-red-800" : "bg-green-100 text-green-800";
    },

    getActionButtonText(status) {
      return status === "Pending" ? "Process" : "View";
    },

    openModal(VisitId) {
      this.selectedVisitId = VisitId;
      this.showModal = true;
    },

    closeModal() {
      this.showModal = false;
      this.selectedVisitId = null;
    },

    handleAppointmentSubmit() { /* reserved */ },

    async getPatientPrescriptions() {
      try {
        this.showLoader = true;
        const response = await apiClient.listPatientPrescriptions();
        if (response.success) {
          this.prescriptions = response.data.visits;
          this.$emit("handleTotals", response.data.total_patients, response.data.total_visits);
        } else {
          this.$toast.error("Failed to fetch patient prescriptions");
        }
      } catch {
        this.$toast.error("Failed to fetch patient prescriptions");
      } finally {
        this.showLoader = false;
      }
    },

    async triggerSearch() {
      try {
        this.showLoader = true;
        const response = await apiClient.searchPatientPrescriptions(this.searchFilters);
        if (response.success) {
          this.prescriptions = response.data.visits;
          this.$emit("handleTotals", response.data.total_patients, response.data.total_visits);
        } else {
          this.$toast.error("Failed to search patient prescriptions");
        }
      } catch {
        this.$toast.error("Failed to search patient prescriptions");
      } finally {
        this.showLoader = false;
      }
    },
  },
};
</script>

<style scoped></style>
